import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import config from '@/config';
import { env } from '@/env';
import { CustomerPricingInterval, type AdminPricingSettings } from '@/modules/api/codegen';
import { InfoLabel } from '@/modules/forms';
import { Button, Heading, Icon, Link, Text } from '@/modules/ui';

import { BillingSection } from './BillingSection';
import * as felaRules from './OrderInfo.rules';

export interface OrderInfoContentProps {
    adminPricingSettings: AdminPricingSettings;
    isFreeTier: boolean;
    onUpgrade: () => void;
}

export const OrderInfoContent = ({ adminPricingSettings, isFreeTier, onUpgrade }: OrderInfoContentProps) => {
    const { css } = useFela();
    const { formatMessage } = useIntl();

    const title = isFreeTier
        ? 'settings.order.type.free'
        : env.VITE_SAAS
          ? 'settings.order.type.workspace'
          : 'settings.order.type.enterprise';

    const email = isFreeTier ? undefined : adminPricingSettings.pricing?.customerEmail;

    return (
        <>
            <div className={css(felaRules.titleContainer)}>
                <Heading variant='h2' elementType='h2' color='foregroundsPrimary'>
                    <FormattedMessage id={title} />
                </Heading>
                {!!email && (
                    <Text
                        variant='ParagraphSmallMedium'
                        color='foregroundsTertiary'
                        data-testid='order-billing-email'
                        aria-label={formatMessage({
                            id: 'settings.order.billingEmail',
                        })}
                    >
                        {email}
                    </Text>
                )}
            </div>

            <BillingSection adminPricingSettings={adminPricingSettings} />

            {isFreeTier ? (
                <Button type='link' size='middle' underline onClick={onUpgrade}>
                    <FormattedMessage id='settings.order.upgradeButton' /> <Icon type='link' width={12} height={12} />
                </Button>
            ) : (
                <Link size='regular' type='native' href={config.links.stripe}>
                    <FormattedMessage id='settings.order.link' /> <Icon type='link' width={12} height={12} />
                </Link>
            )}

            {!env.VITE_SAAS && adminPricingSettings.pricing?.interval !== CustomerPricingInterval.year && (
                <InfoLabel customStyle={felaRules.changeSubscriptionWarning}>
                    <FormattedMessage id='settings.order.changeSubscriptionWarning' />
                </InfoLabel>
            )}
        </>
    );
};
